import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import Icon from './Icon';

interface BidFormProps {
  currentBid: number | null;
  startingPrice: number;
  isActive: boolean;
  onBid: (amount: number) => Promise<void>;
  userId?: string;
  sellerId?: string;
}

export default function BidForm({
  currentBid,
  startingPrice,
  isActive,
  onBid,
  userId,
  sellerId,
}: BidFormProps) {
  const [amount, setAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const minimumBid = currentBid ? currentBid + 100 : startingPrice;
  const minimumBidDisplay = (minimumBid / 100).toFixed(2);

  // Set default bid amount to minimum when it changes
  useEffect(() => {
    if (!amount || parseFloat(amount) * 100 < minimumBid) {
      setAmount(minimumBidDisplay);
    }
  }, [minimumBid, minimumBidDisplay]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || bidding) return;

    const bidAmount = Math.round(parseFloat(amount) * 100);

    if (bidAmount < minimumBid) {
      setError(`Minimum bid is £${minimumBidDisplay}`);
      return;
    }

    setBidding(true);
    setError(null);
    setSuccess(false);

    try {
      await onBid(bidAmount);
      setSuccess(true);
      setAmount('');
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBidding(false);
    }
  }, [amount, bidding, minimumBid, minimumBidDisplay, onBid]);

  const quickBidAmounts = [
    minimumBid,
    minimumBid + 500,  // +£5
    minimumBid + 1000, // +£10
    minimumBid + 2500, // +£25
  ];

  if (userId === sellerId) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-gray-600">You cannot bid on your own auction</p>
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-gray-600">This auction is not accepting bids</p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="bg-primary-50 rounded-lg p-4 text-center">
        <p className="text-primary-700 mb-2">Sign in to place a bid</p>
        <a href="/login" className="btn-primary inline-block">
          Sign In
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick bid buttons */}
      <div className="flex flex-wrap gap-2">
        {quickBidAmounts.map((bidAmount) => (
          <button
            key={bidAmount}
            type="button"
            onClick={() => setAmount((bidAmount / 100).toFixed(2))}
            className={clsx(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              parseFloat(amount) * 100 === bidAmount
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            £{(bidAmount / 100).toFixed(2)}
          </button>
        ))}
      </div>

      {/* Custom bid form */}
      <form onSubmit={handleSubmit} className="flex gap-3">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
            £
          </span>
          <input
            type="number"
            step="0.01"
            min={(minimumBid / 100).toFixed(2)}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError(null);
            }}
            className="input pl-7 text-lg font-medium"
            placeholder={minimumBidDisplay}
          />
        </div>
        <button
          type="submit"
          disabled={bidding || !amount}
          className={clsx(
            'btn-primary px-6 text-lg font-semibold transition-all flex items-center gap-2',
            success && 'bg-green-600 hover:bg-green-700'
          )}
        >
          {bidding ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Bidding...
            </>
          ) : success ? (
            <>
              <Icon name="check_circle" size="small" />
              Bid Placed!
            </>
          ) : (
            <>
              <Icon name="gavel" size="small" />
              Place Bid
            </>
          )}
        </button>
      </form>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Icon name="error_outline" size="small" />
          {error}
        </div>
      )}

      {/* Minimum bid info */}
      <p className="text-sm text-gray-500 text-center">
        Minimum bid: £{minimumBidDisplay}
      </p>
    </div>
  );
}
